import { getMid, asTRBL, getOrientation } from 'diagram-js/lib/layout/LayoutUtil';
import { find } from 'min-dash';
import { Shape, Connection } from 'diagram-js/lib/model';
import {constants} from "@ispm-client/modules/bpmn-modeler/constants/constants";

function calculate(source: Shape, shape: Shape, distance: number) {
  const sourceMid = getMid(source);
  let result;
  switch (shape.type) {
    case 'bpmn:ExclusiveGateway':
      result = {
        x: sourceMid.x,
        y: sourceMid.y + source.height
      };
      break;
    case 'bpmn:Task':
      if (source.type !== 'bpmn:ExclusiveGateway') { // treatment node
        result = {
          x: sourceMid.x,
          y: sourceMid.y + distance
        };
      } else {
        result = getFlowNodePosition(source, shape);
      }
      break;
  }
  return result;
}

export function getVerticalPosition({ source, shape }: { source: Shape, shape: Shape }) {
  return calculate(source, shape, constants.DEFAULT_VERTICAL_DISTANCE);
}

export function getVerticalLayoutVerticalPosition({ source, shape }: { source: Shape, shape: Shape }) {
  return calculate(source, shape, constants.DEFAULT_VERTICAL_LAYOUT_VERTICAL_DISTANCE);
}

/**
 * Always try to place element beneath source;
 * compute actual distance from previous nodes in flow.
 */
function getFlowNodePosition(source: Shape, element) {

  const sourceTrbl = asTRBL(source);
  const sourceMid = getMid(source);

  const position = {
    x: sourceMid.x - (element.width + constants.FLOW_NODE_MARGIN) / 2,
    y: sourceTrbl.bottom + source.height + element.height / 2
  };

  const escapeDirection = {
    x: {
      margin: constants.FLOW_NODE_MARGIN,
      rowSize: constants.FLOW_NODE_COLUMN_SIZE // this naming is part of the expected object structure
    }
  };

  return deconflictPosition(source, element, position, escapeDirection);
}


/**
 * Returns all connected elements around the given source.
 *
 * This includes:
 *
 *   - connected elements
 *   - host connected elements
 *   - attachers connected elements
 *
 */
function getAutoPlaceClosure(source: Shape): Shape[] {

  let allConnected = getConnected(source);

  if (source.host) {
    allConnected = allConnected.concat(getConnected(source.host));
  }

  if (source.attachers) {
    allConnected = allConnected.concat(source.attachers.reduce((shapes, attacher) =>
      shapes.concat(getConnected(attacher))
      , []));
  }

  return allConnected;
}

/**
 * Return target at given position, if defined.
 *
 * This takes connected elements from host and attachers
 * into account, too.
 */
function getConnectedAtPosition(source: Shape, position, element) {
  const bounds = {
    x: position.x - (element.width / 2),
    y: position.y - (element.height / 2),
    width: element.width,
    height: element.height
  };

  const closure = getAutoPlaceClosure(source);

  return find(closure, function (target) {

    if (target === element) {
      return false;
    }

    const orientation = getOrientation(target, bounds, constants.PLACEMENT_DETECTION_PAD);

    return orientation === 'intersect';
  });
}


/**
 * Returns a new, position for the given element
 * based on the given element that is not occupied
 * by some element connected to source.
 *
 * Take into account the escapeDirection (where to move
 * on positioning clashes) in the computation.
 *
 */
function deconflictPosition(source: Shape, element: Shape, position, escapeDelta) {

  function nextPosition(existingElement) {

    const newPosition = {
      x: position.x,
      y: position.y
    };

    ['x', 'y'].forEach(function (axis) {

      const axisDelta = escapeDelta[axis];

      if (!axisDelta) {
        return;
      }

      const dimension = axis === 'x' ? 'width' : 'height';

      const margin = axisDelta.margin,
        rowSize = axisDelta.rowSize;

      if (margin < 0) {
        newPosition[axis] = Math.min(
          existingElement[axis] + margin - element[dimension] / 2,
          position[axis] - rowSize + margin
        );
      } else {
        newPosition[axis] = Math.max(
          existingTarget[axis] + existingTarget[dimension] + margin + element[dimension] / 2,
          position[axis] + rowSize + margin
        );
      }
    });

    return newPosition;
  }

  let existingTarget;

  // deconflict position until free slot is found
  while ((existingTarget = getConnectedAtPosition(source, position, element))) {
    position = nextPosition(existingTarget);
  }

  return position;
}

// helpers //////////////////////

function noneFilter() {
  return true;
}

function getConnected(element, connectionFilter?: (c: Connection) => boolean) {
  return [].concat(
    getTargets(element, connectionFilter),
    getSources(element, connectionFilter)
  );
}

function getSources(shape: Shape, connectionFilter?: (c: Connection) => boolean) {

  if (!connectionFilter) {
    connectionFilter = noneFilter;
  }

  return shape.incoming.filter(connectionFilter).map(function (c) {
    return c.source;
  });
}

function getTargets(shape: Shape, connectionFilter?: (c: Connection) => boolean) {

  if (!connectionFilter) {
    connectionFilter = noneFilter;
  }

  return shape.outgoing.filter(connectionFilter).map(function (c) {
    return c.target;
  });
}
